import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import HomePage from './pages/HomePage';
import RoomPage from './pages/RoomPage';
import UsernameModal from './components/UsernameModal';
import sessionManager from './utils/sessionManager';
import './App.css';

function App() {
  const [darkMode, setDarkMode] = useState(true);
  const [username, setUsername] = useState('');
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [isEditingUsername, setIsEditingUsername] = useState(false);

  useEffect(() => {
    const session = sessionManager.getSession();
    
    // Load dark mode preference
    const prefs = session?.user?.preferences;
    if (prefs?.darkMode !== undefined) {
      setDarkMode(prefs.darkMode);
    }
    
    // Check for existing username
    const savedUsername = sessionManager.getUsername();
    
    if (savedUsername) {
      setUsername(savedUsername);
    } else {
      // Show modal for new users
      setTimeout(() => {
        setShowUsernameModal(true);
      }, 500);
    }
    
    // Activity tracking
    const activityInterval = setInterval(() => {
      sessionManager.updateLastActivity();
    }, 60000);
    
    return () => {
      clearInterval(activityInterval);
    };
  }, []);

  // Sync dark mode to session
  useEffect(() => {
    if (username) {
      sessionManager.updatePreferences({ darkMode });
    }
  }, [darkMode, username]);

  // Save username
  const handleSaveUsername = (name) => {
    const trimmedName = name.trim();
    if (trimmedName.length < 2) {
      alert('Username must be at least 2 characters');
      return;
    }
    
    sessionManager.updateUser({ username: trimmedName });
    setUsername(trimmedName);
    setShowUsernameModal(false);
    setIsEditingUsername(false);

    window.dispatchEvent(new Event('usernameSet'));
  };

  // Open edit modal
  const handleChangeUsername = () => {
    setIsEditingUsername(true);
    setShowUsernameModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    if (username) {
      setShowUsernameModal(false);
      setIsEditingUsername(false);
    }
  };

  return (
    <Router>
      <div className={`app ${darkMode ? 'dark' : 'light'}`}>
        <div className="container">
          <div className="header">
            <h1 className="title">
              <img src="/favicon.png" alt="SyncPlay" className="icon" width="64" height="64"/>
              SyncPlay
            </h1>

            <div className="header-controls">
              {username && (
                  <button
                      className="username-btn"
                      onClick={handleChangeUsername}
                  title="Change username"
                >
                  <span className="username-avatar">
                    {username.charAt(0).toUpperCase()}
                  </span>
                  <span className="username-text">{username}</span>
                  <span className="edit-icon">✎</span>
                </button>
              )}
              
              <button
                className="theme-toggle"
                onClick={() => setDarkMode(!darkMode)}
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? '☀️' : '🌙'}
              </button>
            </div>
          </div>

          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/room/:roomId" element={<RoomPage />} />
          </Routes>
        </div>

        {showUsernameModal && (
          <UsernameModal
            isEditing={isEditingUsername}
            currentUsername={username}
            onSave={handleSaveUsername}
            onClose={handleCloseModal}
          />
        )}
      </div>
    </Router>
  );
}

export default App;